import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { apiCall, apiSend } from "../lib/api";
import { useTypeahead, TypeaheadResults } from "./Typeahead";
import { useRefreshCounts } from "../hooks/useRecords";
import type { OrderRecord } from "../lib/types";

type CustomerGroup = { customerId: string; customerName: string; orders: OrderRecord[] };

function orderItemStatus(record: OrderRecord): { statusClass: string; statusColor: string } {
  const returnStatus = ((record["return-status"] as string) || "").toLowerCase();
  const isNoShow = returnStatus === "no show - tossed";
  const isCompleted =
    returnStatus.includes("picked") || returnStatus.includes("tossed") || returnStatus.includes("mailed");
  if (isNoShow) return { statusClass: "status-no-show", statusColor: "#9b59b6" };
  if (isCompleted) return { statusClass: "status-complete", statusColor: "#4CAF50" };
  if (record["return-status"]) return { statusClass: "status-processing", statusColor: "#2196F3" };
  return { statusClass: "", statusColor: "" };
}

export function SearchTab({ active, runQuery }: { active: boolean; runQuery?: { q: string; n: number } }) {
  const navigate = useNavigate();
  const refreshCounts = useRefreshCounts();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [showTypeahead, setShowTypeahead] = useState(false);
  const suggestions = useTypeahead(query);

  // Injected query (from Cmd+K / typeahead value select on another surface).
  useEffect(() => {
    if (runQuery && runQuery.q) {
      setQuery(runQuery.q);
      setShowTypeahead(false);
      setSubmitted(runQuery.q.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runQuery?.n]);

  const { data, isFetching } = useQuery({
    queryKey: ["search", submitted],
    queryFn: () => apiCall<{ customerGroups: CustomerGroup[] }>(`search?q=${encodeURIComponent(submitted)}`),
    enabled: !!submitted,
  });

  const viewCustomer = (id: string, name: string) => {
    setShowTypeahead(false);
    setQuery(name);
    navigate({ to: "/users/$customerId", params: { customerId: id } });
  };
  const viewOrder = (invoiceId: string) => navigate({ to: "/orders/$invoiceId", params: { invoiceId } });
  const runSearch = (q: string) => {
    setShowTypeahead(false);
    setSubmitted(q.trim());
  };

  async function updateStatus(id: string, value: string, el: HTMLSelectElement) {
    try {
      await apiSend("update-record", "POST", { id, field: "return-status", value });
      el.classList.add("field-saved");
      setTimeout(() => el.classList.remove("field-saved"), 500);
      refreshCounts();
    } catch (e) {
      console.error("Error updating status:", e);
      alert("Failed to update status");
    }
  }

  const groups = data?.customerGroups || [];

  return (
    <div className="tab-content" style={{ display: active ? undefined : "none", textAlign: "center" }}>
      <div className="search-container">
        <input
          type="text"
          id="mainSearchInput"
          placeholder="Search by customer name or twin check number..."
          autoComplete="off"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowTypeahead(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") runSearch(query);
          }}
          onBlur={() => setTimeout(() => setShowTypeahead(false), 200)}
          onFocus={() => query.trim().length >= 2 && setShowTypeahead(true)}
        />
        <div className="typeahead-results" style={{ display: showTypeahead && suggestions.length ? "block" : "none" }}>
          <TypeaheadResults
            suggestions={suggestions}
            onSelectCustomer={viewCustomer}
            onSelectValue={(v) => {
              setQuery(v);
              runSearch(v);
            }}
            onViewOrder={viewOrder}
          />
        </div>
      </div>

      <div id="searchResults">
        {isFetching && <p style={{ textAlign: "center", color: "#666", marginTop: 40 }}>Searching…</p>}
        {!isFetching && submitted && groups.length === 0 && (
          <p style={{ textAlign: "center", color: "#666", marginTop: 40 }}>No results found.</p>
        )}
        {groups.map((group, gi) => {
          const orderWord = group.orders.length === 1 ? "order" : "orders";
          return (
            <div className="search-customer-group" key={gi}>
              <div className="search-customer-header">
                <div className="customer-info">
                  <h3>
                    <span className="customer-link" onClick={() => viewCustomer(group.customerId, group.customerName)}>
                      {group.customerName}
                    </span>
                  </h3>
                  <span className="order-count">
                    {group.orders.length} {orderWord}
                  </span>
                </div>
              </div>
              <div className="search-customer-orders">
                {group.orders.map((record, ri) => {
                  const { statusClass, statusColor } = orderItemStatus(record);
                  const invoiceId = String(record["invoice-id"] || record.rowid);
                  return (
                    <div className={`search-order-item ${statusClass}`} key={ri} onClick={() => viewOrder(invoiceId)}>
                      <div className="search-order-header">
                        <span className="order-date">{record["-date"] || "N/A"}</span>
                        <span className="twin-check-badge">{record["Twin Check Stickers"] || "No Twin Check"}</span>
                      </div>
                      <div className="search-order-details">
                        <div className="order-detail-row">
                          <span className="detail-label">Roll Types:</span>
                          <span className="detail-value">{record["roll-types"] || "N/A"}</span>
                        </div>
                        <div className="order-detail-row">
                          <span className="detail-label">Amount:</span>
                          <span className="detail-value">{(record["amount-of-rolls"] as string) || "0"} rolls</span>
                        </div>
                        <div className="order-detail-row">
                          <span className="detail-label">Return Option:</span>
                          <span className="detail-value">{record["return-option"] || "N/A"}</span>
                        </div>
                        <div className="order-detail-row">
                          <span className="detail-label">Status:</span>
                          <select
                            className="search-status-field"
                            defaultValue={(record["return-status"] as string) || ""}
                            onClick={(e) => e.stopPropagation()}
                            style={{ borderColor: statusColor }}
                            onChange={(e) => {
                              e.stopPropagation();
                              updateStatus(String(record.rowid), e.target.value, e.target);
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="cut">Cut</option>
                            <option value="prepped">Prepped</option>
                            <option value="ready to mail">Ready to Mail</option>
                            <option value="tossed">Tossed</option>
                            <option value="picked-up">Picked Up</option>
                            <option value="mailed">Mailed</option>
                            <option value="no show - tossed">No Show - Tossed</option>
                          </select>
                        </div>
                        <div className="order-detail-row">
                          <span className="detail-label">Prints:</span>
                          <span className={`print-indicator ${record.prints ? "has-prints" : "no-prints"}`}>
                            {(record.prints as string) || "None"}
                          </span>
                        </div>
                        {record.notes && (
                          <div className="order-detail-row full-width">
                            <span className="detail-label">Notes:</span>
                            <span className="detail-value">{record.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
